/**
 * SEO helpers for this SPA.
 *
 * Updates meta tags, canonical, Open Graph, and Twitter cards per route.
 * For crawlers that execute JS (Google), this provides per-page signals.
 * Pair with static sitemap.xml + JSON-LD for strongest coverage without SSR.
 */

import { useEffect } from 'react';

type MetaKey =
  | { name: string; content: string }
  | { property: string; content: string };

function upsertMetaTag(key: MetaKey): void {
  const selector =
    'name' in key
      ? `meta[name="${CSS.escape(key.name)}"]`
      : `meta[property="${CSS.escape(key.property)}"]`;

  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    if ('name' in key) el.setAttribute('name', key.name);
    else el.setAttribute('property', key.property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', key.content);
}

function upsertCanonical(href: string): void {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export type SeoConfig = {
  title: string;
  description?: string;
  canonicalPath: string; // e.g. "/guides" or "/guides/my-slug"
  keywords?: string;
  robots?: string; // e.g. "noindex, nofollow" for admin
  og?: {
    type?: 'website' | 'article';
    title?: string;
    description?: string;
    image?: string;
  };
};

export function applySeo(config: SeoConfig): void {
  document.title = config.title;

  const origin = window.location.origin;
  const canonicalUrl = `${origin}${config.canonicalPath}`;
  upsertCanonical(canonicalUrl);

  if (config.description) {
    upsertMetaTag({ name: 'description', content: config.description });
  }

  if (config.robots) {
    upsertMetaTag({ name: 'robots', content: config.robots });
  } else {
    // Always reset after a noindex SPA page (thin weekly guides).
    upsertMetaTag({ name: 'robots', content: 'index, follow' });
  }

  // OpenGraph tags (index.html already has baseline OG tags; we overwrite per-page)
  upsertMetaTag({ property: 'og:url', content: canonicalUrl });
  upsertMetaTag({ property: 'og:type', content: config.og?.type || 'website' });
  upsertMetaTag({ property: 'og:title', content: config.og?.title || config.title });
  if (config.og?.description || config.description) {
    upsertMetaTag({ property: 'og:description', content: config.og?.description || config.description || '' });
  }

  // OG Image
  if (config.og?.image) {
    const origin = window.location.origin;
    const ogImageUrl = config.og.image.startsWith('http') ? config.og.image : `${origin}${config.og.image}`;
    upsertMetaTag({ property: 'og:image', content: ogImageUrl });
  }

  // Keywords
  if (config.keywords) {
    upsertMetaTag({ name: 'keywords', content: config.keywords });
  }

  const twitterTitle = config.og?.title || config.title;
  const twitterDescription = config.og?.description || config.description || '';
  upsertMetaTag({ name: 'twitter:card', content: 'summary_large_image' });
  upsertMetaTag({ name: 'twitter:title', content: twitterTitle });
  if (twitterDescription) {
    upsertMetaTag({ name: 'twitter:description', content: twitterDescription });
  }
  if (config.og?.image) {
    const ogImageUrl = config.og.image.startsWith('http')
      ? config.og.image
      : `${origin}${config.og.image.startsWith('/') ? config.og.image : `/${config.og.image}`}`;
    upsertMetaTag({ name: 'twitter:image', content: ogImageUrl });
  }
}

/** Apply SEO on mount (static pages) or when config changes (dynamic pages). */
export function usePageSeo(config: SeoConfig | null | undefined): void {
  useEffect(() => {
    if (!config) return;
    applySeo(config);
  }, [
    config?.title,
    config?.description,
    config?.canonicalPath,
    config?.keywords,
    config?.robots,
    config?.og?.title,
    config?.og?.description,
    config?.og?.image,
    config?.og?.type,
  ]);
}


